import { useEffect, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import Api from "../Network";
import { useDispatch } from "react-redux";
import { setCompany } from "../redux/action";

// Track if a fetch is in progress globally to prevent duplicate requests
let isFetchingCompany = false;
let fetchPromise: Promise<any> | null = null;

// Company module ("model") titles, as returned in `main_settings.models`.
export const COMPANY_MODELS = {
    ECOMMERCE: "manage_ecommerce",
    ACTIVITY: "activity",
    INSURANCE: "insurance",
    SUPPLIERS: "manage_suppliers",
    SALLA: "salla_integration",
    DMS: "dms",
    LOYALTY: "loyalty",
    /** Rx review module (pharmacist workspace, statuses, reject reasons). */
    PRESCRIPTION: "prescription",
    /** Refund workflow (refund requests, approval rules, refunds report). */
    REFUND: "refund",
} as const;

export const hasCompanyModel = (company: any, title: string): boolean => {
    const matches = (model: any) => model?.title === title;
    return !!(
        company?.main_settings?.models?.some(matches) ||
        company?.models?.some(matches)
    );
};

/** Company has shopper/DMS feature flag on (`main_settings.more_settings.use_shopper`). */
export const companyUsesShopper = (company: any): boolean =>
    Number(company?.main_settings?.more_settings?.use_shopper) === 1;

/** DMS UI/routes require both the `dms` model and `use_shopper === 1`. */
export const companyCanUseDms = (company: any): boolean =>
    hasCompanyModel(company, COMPANY_MODELS.DMS) && companyUsesShopper(company);

const useCompany = () => {
    const dispatch = useDispatch();
    // Get company from Redux store - this is the source of truth
    const reduxCompany = useSelector((state: any) => state.setReducer?.company);
    // React state so consumers re-render when the fetch starts/finishes
    // (module-level isFetchingCompany alone does not trigger updates).
    const [loading, setLoading] = useState(!reduxCompany);

    // Fetch company data from the API only if not in Redux
    const fetchCompany = useCallback(async () => {
        // If company already exists in Redux, don't fetch
        if (reduxCompany) {
            setLoading(false);
            return;
        }

        // If already fetching, wait for the existing promise
        if (isFetchingCompany && fetchPromise) {
            setLoading(true);
            try {
                await fetchPromise;
            } catch (error) {
                // Existing fetch failed; fall through so this caller can retry
            }
            // Re-check via Redux on next render; mark done if still null
            setLoading(false);
            return;
        }

        try {
            isFetchingCompany = true;
            setLoading(true);
            fetchPromise = Api.get("company");
            const response = await fetchPromise;

            if (response) {
                dispatch(setCompany(response));
            } else {
                console.error("Invalid company response:", response);
            }
        } catch (error) {
            console.error("Error fetching company:", error);
        } finally {
            isFetchingCompany = false;
            fetchPromise = null;
            setLoading(false);
        }
    }, [dispatch, reduxCompany]);

    // Update company data in the Redux store
    const updateCompanyState = useCallback(
        (newCompany: any) => {
            dispatch(setCompany(newCompany));
        },
        [dispatch]
    );

    // Only fetch company data once on mount if it doesn't exist in Redux
    useEffect(() => {
        if (!reduxCompany) {
            fetchCompany();
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount

    const hasModel = useCallback(
        (title: string) => hasCompanyModel(reduxCompany, title),
        [reduxCompany]
    );

    return {
        company: reduxCompany,
        hasModel,
        setCompany: updateCompanyState,
        fetchCompany,
        loading: loading && !reduxCompany,
    };
};

export default useCompany;
